import React, { useState } from 'react';
import { X, Calendar, MapPin, Link, FileText, Upload, Download, Users, Edit2, Save, XCircle } from 'lucide-react';
import { meetingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FilePreviewModal from './FilePreviewModal';
import toast from 'react-hot-toast';

interface MeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicationId: number;
  publicationTitle: string;
  onMeetingCreated?: () => void;
  leadAuthorId?: number;
}

interface Meeting {
  id: number;
  meeting_type: 'in_person' | 'online';
  room_number?: string;
  meeting_link?: string;
  meeting_notes?: string;
  meeting_date?: string;
  created_at: string;
  first_name: string;
  last_name: string;
  files: Array<{
    id: number;
    original_filename: string;
    file_size: number;
    file_type: string;
    uploaded_at: string;
  }>;
}

const MeetingModal: React.FC<MeetingModalProps> = ({
  isOpen,
  onClose,
  publicationId,
  publicationTitle,
  onMeetingCreated,
  leadAuthorId
}) => {
  const { user } = useAuth();
  const handleClose = () => {
    onClose();
  };
  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create');
  const [isLoading, setIsLoading] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    meetingType: 'in_person' as 'in_person' | 'online',
    roomNumber: '',
    meetingLink: '',
    meetingNotes: '',
    meetingDate: '',
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  
  // File preview modal state
  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);

  // Meeting notes editing state
  const [editingMeetingId, setEditingMeetingId] = useState<number | null>(null);
  const [editingNotes, setEditingNotes] = useState<string>('');
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (formData.meetingType === 'in_person' && !formData.roomNumber.trim()) {
      toast.error('Room number is required for in-person meetings');
      return;
    }
    
    if (formData.meetingType === 'online' && !formData.meetingLink.trim()) {
      toast.error('Meeting link is required for online meetings');
      return;
    }

    setIsLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('publicationId', publicationId.toString());
      formDataToSend.append('meetingType', formData.meetingType);
      formDataToSend.append('roomNumber', formData.roomNumber);
      formDataToSend.append('meetingLink', formData.meetingLink);
      formDataToSend.append('meetingNotes', formData.meetingNotes);
      formDataToSend.append('meetingDate', formData.meetingDate);
      
      // Add files
      selectedFiles.forEach((file, index) => {
        formDataToSend.append('files', file);
      });

      const response = await meetingAPI.createMeeting(formDataToSend);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publicationId}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success(`Meeting created successfully! ${response.filesUploaded} files uploaded.`);
      
      // Reset form
      setFormData({
        meetingType: 'in_person',
        roomNumber: '',
        meetingLink: '',
        meetingNotes: '',
        meetingDate: '',
      });
      setSelectedFiles([]);
      
      // Refresh meetings list
      if (activeTab === 'history') {
        loadMeetings();
      }
      
      onMeetingCreated?.();
      
      // Close the modal after successful creation
      handleClose();
      
    } catch (error: any) {
      console.error('Error creating meeting:', error);
      toast.error(error.response?.data?.error || 'Failed to create meeting');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMeetings = async () => {
    setIsLoadingMeetings(true);
    try {
      const meetingsData = await meetingAPI.getMeetings(publicationId);
      setMeetings(meetingsData);
    } catch (error: any) {
      console.error('Error loading meetings:', error);
      toast.error('Failed to load meetings');
    } finally {
      setIsLoadingMeetings(false);
    }
  };

  const handleDownloadFile = async (meetingId: number, fileId: number, filename: string) => {
    try {
      const blob = await meetingAPI.downloadFile(meetingId, fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePreviewFile = (meetingId: number, file: any) => {
    setSelectedMeetingId(meetingId);
    setSelectedFile(file);
    setIsFilePreviewModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if user can edit meeting notes
  const canEditMeetingNotes = () => {
    if (!user) return false;
    return user.role === 'superadmin' || 
           user.role === 'admin' || 
           (leadAuthorId && user.id === leadAuthorId);
  };

  // Start editing meeting notes
  const startEditingNotes = (meetingId: number, currentNotes: string) => {
    setEditingMeetingId(meetingId);
    setEditingNotes(currentNotes || '');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMeetingId(null);
    setEditingNotes('');
  };

  // Save meeting notes
  const saveMeetingNotes = async (meetingId: number) => {
    setIsUpdatingNotes(true);
    try {
      await meetingAPI.updateMeetingNotes(meetingId, editingNotes);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publicationId}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Meeting notes updated successfully');
      
      // Update the local state
      setMeetings(prev => prev.map(meeting => 
        meeting.id === meetingId 
          ? { ...meeting, meeting_notes: editingNotes }
          : meeting
      ));
      
      setEditingMeetingId(null);
      setEditingNotes('');
    } catch (error: any) {
      console.error('Error updating meeting notes:', error);
      toast.error(error.response?.data?.error || 'Failed to update meeting notes');
    } finally {
      setIsUpdatingNotes(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Only close if clicking on the backdrop, not on the modal content
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Meeting Management</h2>
            <p className="text-sm text-gray-600 mt-1">{publicationTitle}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'create'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Create Meeting
          </button>
          <button
            onClick={() => {
              setActiveTab('history');
              loadMeetings();
            }}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Meeting History
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'create' ? (
            <form 
              onSubmit={handleSubmit} 
              className="space-y-6"
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onBlur={(e) => e.stopPropagation()}
            >
              {/* Meeting Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Meeting Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="meetingType"
                      value="in_person"
                      checked={formData.meetingType === 'in_person'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">In-Person</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="meetingType"
                      value="online"
                      checked={formData.meetingType === 'online'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Online</span>
                  </label>
                </div>
              </div>

              {/* Meeting Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Meeting Date & Time
                </label>
                <input
                  type="datetime-local"
                  name="meetingDate"
                  value={formData.meetingDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Room Number or Meeting Link */}
              {formData.meetingType === 'in_person' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="h-4 w-4 inline mr-2" />
                    Room Number *
                  </label>
                  <input
                    type="text"
                    name="roomNumber"
                    value={formData.roomNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., Room 101, Conference Room A"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Link className="h-4 w-4 inline mr-2" />
                    Meeting Link *
                  </label>
                  <input
                    type="url"
                    name="meetingLink"
                    value={formData.meetingLink}
                    onChange={handleInputChange}
                    placeholder="https://zoom.us/j/123456789 or https://teams.microsoft.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              {/* Meeting Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="h-4 w-4 inline mr-2" />
                  Meeting Notes (Optional)
                </label>
                <textarea
                  name="meetingNotes"
                  value={formData.meetingNotes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Add any notes about the meeting agenda, objectives, or important points..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Upload className="h-4 w-4 inline mr-2" />
                  Upload Files (Optional)
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.r,.py,.pkl,.model,.R,.PY,.PKL,.MODEL"
                  onChange={handleFileChange}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  onBlur={(e) => e.stopPropagation()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: PDF, Word, PowerPoint, Text, Images, Excel, R, Python, Pickle, Model files (Max 50MB per file)
                </p>
                
                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium text-gray-700">Selected Files:</p>
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{file.name} ({formatFileSize(file.size)})</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Meeting'}
                </button>
              </div>
            </form>
          ) : (
            /* Meeting History */
            <div>
              {isLoadingMeetings ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : meetings.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No meetings scheduled yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {meetings.map((meeting, index) => (
                    <div key={meeting.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">
                              {index + 1}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              meeting.meeting_type === 'in_person' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {meeting.meeting_type === 'in_person' ? 'In-Person' : 'Online'}
                            </span>
                            <span className="text-sm text-gray-500">
                              by {meeting.first_name} {meeting.last_name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {meeting.meeting_date ? formatDate(meeting.meeting_date) : 'No date set'}
                          </p>
                          {meeting.meeting_type === 'in_person' && meeting.room_number && (
                            <p className="text-sm text-gray-600">
                              <MapPin className="h-3 w-3 inline mr-1" />
                              {meeting.room_number}
                            </p>
                          )}
                          {meeting.meeting_type === 'online' && meeting.meeting_link && (
                            <a 
                              href={meeting.meeting_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              <Link className="h-3 w-3 inline mr-1" />
                              Join Meeting
                            </a>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDate(meeting.created_at)}
                        </span>
                      </div>
                      
                      {/* Meeting Notes */}
                      <div className="mb-3">
                        {editingMeetingId === meeting.id ? (
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Meeting Notes
                            </label>
                            <textarea
                              value={editingNotes}
                              onChange={(e) => setEditingNotes(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter meeting notes..."
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={() => saveMeetingNotes(meeting.id)}
                                disabled={isUpdatingNotes}
                                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center space-x-1"
                              >
                                <Save className="h-3 w-3" />
                                <span>{isUpdatingNotes ? 'Saving...' : 'Save'}</span>
                              </button>
                              <button
                                onClick={cancelEditing}
                                disabled={isUpdatingNotes}
                                className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 flex items-center space-x-1"
                              >
                                <XCircle className="h-3 w-3" />
                                <span>Cancel</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {meeting.meeting_notes ? (
                                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                                  {meeting.meeting_notes}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded">
                                  No meeting notes
                                </p>
                              )}
                            </div>
                            {canEditMeetingNotes() && (
                              <button
                                onClick={() => startEditingNotes(meeting.id, meeting.meeting_notes || '')}
                                className="ml-2 p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Edit meeting notes"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {meeting.files && meeting.files.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Attached Files:</p>
                          <div className="space-y-1">
                            {meeting.files.map((file) => {
                              const fileExtension = file.original_filename.split('.').pop()?.toLowerCase() || '';
                              const canPreview = ['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);
                              
                              
                              return (
                                <div key={file.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                  <span className="text-sm text-gray-700 flex-1 mr-2">{file.original_filename}</span>
                                  <div className="flex space-x-2">
                                    {canPreview && (
                                      <button
                                        onClick={() => handlePreviewFile(meeting.id, file)}
                                        className="text-green-600 hover:text-green-800 text-sm"
                                        title="Preview"
                                      >
                                        Preview
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDownloadFile(meeting.id, file.id, file.original_filename)}
                                      className="text-blue-600 hover:text-blue-800"
                                      title="Download"
                                    >
                                      <Download className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <FilePreviewModal
        isOpen={isFilePreviewModalOpen}
        onClose={() => setIsFilePreviewModalOpen(false)}
        meetingId={selectedMeetingId || 0}
        file={selectedFile}
      />
    </div>
  );
};

export default MeetingModal;
